import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { useTranslate } from "src/locales";
import { requestService } from "src/api/request/requestService";

export interface UseServiceValidationProps {
  selectedServicesData: any[];
  setSelectedServicesData: React.Dispatch<React.SetStateAction<any[]>>;
  setSelectedServices: React.Dispatch<React.SetStateAction<(number | string)[]>>;
  
  patientAge: number | null;
  patientHeight: string;
  patientGender: string | null;
  rowID: string;

  setOpenVkiModal: (val: boolean) => void;
  setOpenEgfrModal: (val: boolean) => void;
  setOpenDirectionModal: (val: boolean) => void;
  setOpenAskDoctorModal: (val: boolean) => void;
  setOpenPregnantModal: (val: boolean) => void;
  setOpenHivModal: (val: boolean) => void;
  setOpenPacemakerModal: (val: boolean) => void;
  setOpenLabModal: (val: boolean) => void;
  setOpenInvestigationModal: (val: boolean) => void;
  
  pendingAddToPatient: boolean;
  setPendingAddToPatient: (val: boolean) => void;
  isSubmittingToPatient: boolean;
  setIsSubmittingToPatient: (val: boolean) => void;

  serviceAddInfo: Record<string, any>;
  setServiceAddInfo: React.Dispatch<React.SetStateAction<Record<string, any>>>;

  pendingConfirmSelection?: any;

  onClose: () => void;
}

export function useServiceValidation({
  selectedServicesData,
  setSelectedServicesData,
  setSelectedServices,
  patientAge,
  patientHeight,
  patientGender,
  rowID,
  
  setOpenVkiModal,
  setOpenEgfrModal,
  setOpenDirectionModal,
  setOpenAskDoctorModal,
  setOpenPregnantModal,
  setOpenHivModal,
  setOpenPacemakerModal,
  setOpenLabModal,
  setOpenInvestigationModal,
  
  pendingAddToPatient,
  setPendingAddToPatient,
  isSubmittingToPatient,
  setIsSubmittingToPatient,

  serviceAddInfo,
  setServiceAddInfo,
  pendingConfirmSelection,

  onClose,
}: UseServiceValidationProps) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const [doctorModalData, setDoctorModalData] = useState<any>(null);

  // --- Helpers ---

  const directionTextToNumberMap = useMemo(() => {
    const map: Record<string, number> = {
      [t("patient-request-service.direction-options.right")]: 1,
      [t("patient-request-service.direction-options.left")]: 2,
      [t("patient-request-service.direction-options.none")]: 3,
      "Yok": 0,
      "Sağ": 1,
      "Sol": 2,
      "Ayrım Yok": 3,
      "Sağ-sol ayrımı yok": 3,
      "Right": 1,
      "Left": 2,
      "No Difference": 3,
      "1": 1,
      "2": 2,
      "3": 3,
    };
    return map;
  }, [t]);

  const isFemale = useCallback((g?: string | null) => {
    if (!g) return false;
    const val = String(g).toLowerCase().trim();
    return val === "f" || val === "k" || val === "female" || val === "kadın" || val === "kadin" || val === "2";
  }, []);

  const getDirectionToNumber = (direction: string): number | undefined => {
    if (!direction) return undefined;
    const normalized = direction.trim();
    const value = directionTextToNumberMap[normalized];
    return value !== undefined ? value : undefined;
  };

  const upsertAddInfoForPendingService = (key: string, data: any, serviceId?: number | string) => {
    const targetId = serviceId || pendingConfirmSelection?.serviceId;
    if (!targetId) return;
    setServiceAddInfo((prev) => ({
      ...prev,
      [String(targetId)]: {
        ...(prev[String(targetId)] || {}),
        [key]: data,
      },
    }));
  };

  // --- Check Functions ---

  const checkVkiRequired = () => {
    return selectedServicesData.some((service: any) => {
      const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
      return controlType === "VKİ" || controlType === "VKI" || controlType === "vki";
    });
  };

  const checkEgfrRequired = () => {
    const hasEgfrService = selectedServicesData.some((service: any) => {
      const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
      return (
        controlType === "Çocuk eGFR" ||
        controlType === "Cocuk eGFR" ||
        controlType === "ÇOCUK EGFR" ||
        controlType === "cocuk egfr"
      );
    });
    if (!hasEgfrService) return false;

    if (patientAge !== null && patientAge >= 18) {
      return false;
    }

    return true;
  };

  const checkDirectionRequired = () => {
    const hasDirectionService = selectedServicesData.some((service: any) => {
      const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
      const needsDirection =
        controlType === "İşlem Yönü" ||
        controlType === "Islem Yonu" ||
        controlType === "İSLEM YÖNÜ" ||
        controlType === "İşlem Yonu" ||
        controlType === "ISLEM YONU" ||
        controlType === "İşlem Yönü Sor" ||
        controlType === "İşlem Yönü - Sor" ||
        controlType === "Sor";

      const alreadyHasDirection = !!(service.direction && String(service.direction).trim());
      return needsDirection && !alreadyHasDirection;
    });

    return hasDirectionService;
  };

  const checkDoctorRequired = () => {
    const hasDoctorService = selectedServicesData.some((service: any) => {
      const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
      const needsDoctor =
        controlType === "Hizmet Girişinde ve İstek Onaylandığında Sor" ||
        controlType === "Hizmet Girişinde Yapıldığında Sor" ||
        controlType === "Doktor Sor" ||
        controlType === "Doktor Sor - Hizmet Girişinde ve İstek Onaylandığında Sor" ||
        controlType === "Doktor Sor - Hizmet Girişinde Yapıldığında Sor";

      const alreadyHasDoctor = !!(service.askDoctor && service.askDoctor.doctorName);
      return needsDoctor && !alreadyHasDoctor;
    });
    return hasDoctorService;
  };

  const checkPregnantRequired = () => {
    const hasPregnantService = selectedServicesData.some((service: any) => {
      const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
      const needsPregnant =
        controlType === "Hamile Kontrolü" ||
        controlType === "Hamile Kontrolu" ||
        controlType === "Hamile" ||
        controlType === "Pregnancy";
      const already = !!service.pregnantInfo;
      return needsPregnant && !already;
    });

    if (!hasPregnantService) return false;
    if (!isFemale(patientGender)) return false;
    return true;
  };

  const checkHivRequired = () => {
    return selectedServicesData.some((service: any) => {
      const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
      const needs =
        controlType === "HIV Testi" ||
        controlType === "HIV" ||
        controlType === "HIV TEST" ||
        controlType === "Hiv Testi";
      const already = !!service.hivProcess;
      return needs && !already;
    });
  };
  const checkPacemakerRequired = () => {
    return selectedServicesData.some((service: any) => {
      const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
      const needs =
        controlType === "Kalp Pili Kontrolü" ||
        controlType === "Kalp Pili" ||
        controlType === "Pacemaker" ||
        controlType === "Kalp Pili Kontrolu";
      const already = !!service.pacemakerInfo;
      return needs && !already;
    });
  };

  const checkLabRequired = () => {
    return selectedServicesData.some((service: any) => {
      const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
      const needs =
        controlType === "Laboratuvar" ||
        controlType === "Laboratory" ||
        controlType === "Lab" ||
        controlType === "LAB";
      const already = !!service.labSampleRegion;
      return needs && !already;
    });
  };

  const checkInvestigationRequired = () => {
    return selectedServicesData.some((service: any) => {
      const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
      const needs =
        controlType === "Tetkik İstem Nedeni Zorunlu" ||
        controlType === "Tetkik Istem Nedeni Zorunlu" ||
        controlType === "Tetkik" ||
        controlType === "Investigation Reason";
      const already = !!service.investigationNote;
      return needs && !already;
    });
  };

  // --- Submission ---

  const performAddToPatient = async (overrideAddInfo?: Record<string, any>, overrideServicesData?: any[]) => {
    try {
      if (!rowID) {
        enqueueSnackbar(t("patient-request-service.notifications.validation.patient-id-missing"), {
          variant: "error",
          autoHideDuration: 4000,
        });
        return;
      }

      const patientId = parseInt(rowID, 10);
      if (Number.isNaN(patientId)) {
        enqueueSnackbar(t("patient-request-service.notifications.validation.patient-id-invalid"), {
          variant: "error",
          autoHideDuration: 4000,
        });
        return;
      }

      const convertAddNewInfoKeys = (addnewinfo: any): any => {
        if (!addnewinfo) return undefined;
        const converted: any = {};
        Object.keys(addnewinfo).forEach((key) => {
          const lowerKey = key.toLowerCase();
          converted[lowerKey] = addnewinfo[key];
        });
        return Object.keys(converted).length > 0 ? converted : undefined;
      };

      const processTime = dayjs().format("YYYY-MM-DDTHH:mm:ss");

      const currentServicesData = overrideServicesData || selectedServicesData;
      console.log("performAddToPatient - Current Services Data:", currentServicesData.map(s => s.id));
      console.log("performAddToPatient - Current Add Info:", overrideAddInfo || serviceAddInfo);

      const items = currentServicesData.map((service: any) => {
        const serviceId = service.process_id || service.id;
        const lookupId = String(serviceId);
        const quantity = Number(service.quantity || 1);
        const olrId = service.direction ? getDirectionToNumber(service.direction) : undefined;

        const osIdSource =
          service.generalSessionId ??
          service.sessionId ??
          service.session_id ??
          service.osId ??
          service.os_id;

        const parsedOsId =
          osIdSource !== undefined && osIdSource !== null && osIdSource !== ""
            ? Number(osIdSource)
            : undefined;
        
        const currentAddInfoState = overrideAddInfo || serviceAddInfo;
        const addInfo = currentAddInfoState[lookupId] || undefined;
        console.log(`Processing service ${lookupId}. Found addInfo:`, addInfo);
        const olrIdFromDirection = addInfo?.PROCESS_DIRECTION?.OlrId;

        return {
          ServiceId: Number(serviceId),
          Quantity: quantity,
          ...(parsedOsId !== undefined && !Number.isNaN(parsedOsId) && { OsId: parsedOsId }),
          ...(olrId !== undefined && { OlrId: olrId }),
          ...(olrIdFromDirection !== undefined && { OlrId: olrIdFromDirection }),
          addnewinfo:
            addInfo && Object.keys(addInfo).length > 0 ? addInfo : undefined,
        };
      });

      const validItems = items.filter(
        (item) =>
          Number.isFinite(item.ServiceId) &&
          !Number.isNaN(item.ServiceId) &&
          Number.isFinite(item.Quantity) &&
          !Number.isNaN(item.Quantity)
      );

      if (validItems.length === 0) {
        enqueueSnackbar(t("patient-request-service.notifications.validation.no-valid-service"), {
          variant: "warning",
          autoHideDuration: 3000,
        });
        return;
      }

      const requestBody = {
        ptid: patientId,
        items: validItems.map((item) => ({
          processtime: processTime,
          serviceid: item.ServiceId,
          quantity: item.Quantity,
          ...(item.OsId !== undefined && { osid: item.OsId }),
          ...(item.OlrId !== undefined && { olrid: item.OlrId }),
          ...(item.addnewinfo && { addnewinfo: convertAddNewInfoKeys(item.addnewinfo) }),
        })),
      };

      setIsSubmittingToPatient(true);
      const response = await requestService.addProcessToPatFileBulk(requestBody as any);

      if (response.issuccess) {
        if (response.data?.success === false && response.data?.errors?.length > 0) {
          const errorMessages = response.data.errors
            .map((err: any) => err.errormessage || t("patient-request-service.notifications.add-process.unknown-error"))
            .join("\n");
          
          enqueueSnackbar(errorMessages, {
            variant: "error",
            autoHideDuration: 6000,
            style: { whiteSpace: "pre-line" },
          });
        } else {
          enqueueSnackbar(t("patient-request-service.notifications.add-process.success"), {
            variant: "success",
            autoHideDuration: 3000,
          });
          setSelectedServices([]);
          setSelectedServicesData([]);
          onClose();
        }
      } else {
        enqueueSnackbar(
          response.message || t("patient-request-service.notifications.add-process.warning"),
          {
            variant: "error",
            autoHideDuration: 5000,
          }
        );
      }
    } catch (error) {
      console.error("Error creating patient request:", error);
      enqueueSnackbar(t("patient-request-service.notifications.add-process.error"), {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setIsSubmittingToPatient(false);
      setPendingAddToPatient(false);
    }
  };

  const handleAddToPatientClick = async () => {
    try {
      if (isSubmittingToPatient) {
        return;
      }

      console.log("handleAddToPatientClick - Validating Services:", JSON.stringify(selectedServicesData, null, 2));

      if (selectedServicesData.length === 0) {
        enqueueSnackbar(t("patient-request-service.notifications.validation.no-service-selected"), {
          variant: "warning",
          autoHideDuration: 3000,
        });
        return;
      }

      if (checkVkiRequired()) {
        setPendingAddToPatient(true);
        setOpenVkiModal(true);
        return;
      }

      if (checkEgfrRequired()) {
        setPendingAddToPatient(true);
        setOpenEgfrModal(true);
        return;
      }

      if (checkDirectionRequired()) {
        setPendingAddToPatient(true);
        setOpenDirectionModal(true);
        return;
      }

      if (checkDoctorRequired()) {
        setPendingAddToPatient(true);
        setOpenAskDoctorModal(true);
        return;
      }

      if (checkPregnantRequired()) {
        setPendingAddToPatient(true);
        setOpenPregnantModal(true);
        return;
      }

      if (checkHivRequired()) {
        setPendingAddToPatient(true);
        setOpenHivModal(true);
        return;
      }

      if (checkPacemakerRequired()) {
        setPendingAddToPatient(true);
        setOpenPacemakerModal(true);
        return;
      }

      if (checkLabRequired()) {
        setPendingAddToPatient(true);
        setOpenLabModal(true);
        return;
      }

      if (checkInvestigationRequired()) {
        setPendingAddToPatient(true);
        setOpenInvestigationModal(true);
        return;
      }

      await performAddToPatient();
    } catch (error) {
      console.error("Error in handleAddToPatientClick:", error);
    }
  };
  // --- Handlers ---

  const handleVkiModalSave = async (data?: any) => {
    let newAddInfo: Record<string, any> | undefined = undefined;

    if (data) {
      const servicesToUpdate = selectedServicesData.filter((service: any) => {
        const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
        return controlType === "VKİ" || controlType === "VKI" || controlType === "vki";
      });

      if (servicesToUpdate.length > 0) {
        console.log("Saving VKI info for services:", servicesToUpdate.map(s => s.id));
        setServiceAddInfo((prev) => {
          const next = { ...prev };
          servicesToUpdate.forEach((svc: any) => {
            const id = String(svc.process_id || svc.id);
            console.log("Setting serviceAddInfo for id:", id, "Data:", data);
            next[id] = { 
              ...(next[id] || {}), 
              HEIGHT_INFO: {
                Height: data?.height ? Number(data.height) : null,
                Weight: data?.weight ? Number(data.weight) : null,
                VKI: data?.bmi ? Number(data.bmi) : null,
                PsychologicalStatusId: data?.psychStatus ? Number(data.psychStatus) : null,
                MarriageId: data?.maritalStatus ? Number(data.maritalStatus) : null,
              }
            };
          });
          newAddInfo = next;
          return next;
        });
      } else {
        console.warn("No services found requiring VKI update. Checking control_type...", selectedServicesData.map(s => ({ id: s.id, type: s.control_type || s.controlType || s.hiz_ist_kont_turu })));
      }
    }
    setOpenVkiModal(false);
    if (pendingAddToPatient) {
      console.log("Calling performAddToPatient from handleVkiModalSave. New Info:", newAddInfo);
      await performAddToPatient(newAddInfo || serviceAddInfo);
    }
  };

  const handleEgfrModalSave = async (data?: any) => {
    let newAddInfo: Record<string, any> | undefined = undefined;

    if (data) {
      const servicesToUpdate = selectedServicesData.filter((service: any) => {
          const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
          return (
            controlType === "Çocuk eGFR" ||
            controlType === "Cocuk eGFR" ||
            controlType === "ÇOCUK EGFR" ||
            controlType === "cocuk egfr"
          );
      });

      if (servicesToUpdate.length > 0) {
        setServiceAddInfo((prev) => {
          const next = { ...prev };
          servicesToUpdate.forEach((svc: any) => {
            const id = String(svc.process_id || svc.id);
            next[id] = { 
              ...(next[id] || {}), 
              EGFR: {
                Height: data?.height ? Number(data.height) : null,
                PatientMuscleFactor: data?.muscleFactor ? Number(data.muscleFactor) : null,
              } 
            };
          });
          newAddInfo = next;
          return next;
        });
      }
    }
    setOpenEgfrModal(false);
    if (pendingAddToPatient) {
      await performAddToPatient(newAddInfo || serviceAddInfo);
    }
  };

  const handleDirectionSave = async (directionLabel: string) => {
    let nextServicesData = selectedServicesData;
    
    setSelectedServicesData((prev) => {
        const next = prev.map((service: any) => {
        const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
        const needsDirection =
          controlType === "İşlem Yönü" ||
          controlType === "Islem Yonu" ||
          controlType === "İSLEM YÖNÜ" ||
          controlType === "İşlem Yonu" ||
          controlType === "ISLEM YONU" ||
          controlType === "İşlem Yönü Sor" ||
          controlType === "İşlem Yönü - Sor" ||
          controlType === "Sor";

        if (needsDirection) {
          return { ...service, direction: directionLabel };
        }
        return service;
      });
      nextServicesData = next;
      return next;
    });

    setOpenDirectionModal(false);
    if (pendingAddToPatient) {
      await performAddToPatient(undefined, nextServicesData);
    }
  };

  const handleAskDoctorSave = async (data: any) => {
    let newAddInfo: Record<string, any> | undefined = undefined;
    let nextServicesData = selectedServicesData;
    const servicesToUpdate: any[] = [];

    setSelectedServicesData((prev) => {
      const next = prev.map((service: any) => {
        const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
        const needsDoctor =
          controlType === "Hizmet Girişinde ve İstek Onaylandığında Sor" ||
          controlType === "Hizmet Girişinde Yapıldığında Sor" ||
          controlType === "Doktor Sor" ||
          controlType === "Doktor Sor - Hizmet Girişinde ve İstek Onaylandığında Sor" ||
          controlType === "Doktor Sor - Hizmet Girişinde Yapıldığında Sor";

        if (needsDoctor) {
          servicesToUpdate.push(service);
          return { ...service, askDoctor: data };
        }
        return service;
      });
      nextServicesData = next;
      return next;
    });

    if (servicesToUpdate.length > 0) {
       setServiceAddInfo((prev) => {
          const next = { ...prev };
          servicesToUpdate.forEach((svc: any) => {
            const id = String(svc.process_id || svc.id);
            next[id] = { 
              ...(next[id] || {}), 
              ASK_DOCTOR_REQUEST: {
                PatientTypeId: data?.arrivalType ? Number(data.arrivalType) : null,
                DeptId: data?.departmentName ? Number(data.departmentName) : null,
                DoctorId: data?.doctorName ? Number(data.doctorName) : null,
                RefDoctorId: data?.referenceDoctor ? Number(data.referenceDoctor) : null,
                AssistantDoctorId: data?.assistantDoctor ? Number(data.assistantDoctor) : null,
                RefPersonelId: data?.referringPersonnel ? Number(data.referringPersonnel) : null,
              }
            };
          });
          newAddInfo = next;
          return next;
        });
    }

    setDoctorModalData(data);
    setOpenAskDoctorModal(false);
    if (pendingAddToPatient) {
      await performAddToPatient(newAddInfo || serviceAddInfo, nextServicesData);
    }
  };

  const handlePregnantSave = async (value: string) => {
    let newAddInfo: Record<string, any> | undefined = undefined;
    let nextServicesData = selectedServicesData;
    const servicesToUpdate: any[] = [];

    setSelectedServicesData((prev) => {
      const next = prev.map((service: any) => {
        const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
        const needsPregnant =
          controlType === "Hamile Kontrolü" ||
          controlType === "Hamile Kontrolu" ||
          controlType === "Hamile" ||
          controlType === "Pregnancy";
        if (needsPregnant) {
          servicesToUpdate.push(service);
          return { ...service, pregnantInfo: value };
        }
        return service;
      });
      nextServicesData = next;
      return next;
    });

    if (servicesToUpdate.length > 0) {
       setServiceAddInfo((prev) => {
          const next = { ...prev };
          servicesToUpdate.forEach((svc: any) => {
            const id = String(svc.process_id || svc.id);
            next[id] = { ...(next[id] || {}), PREGNANT_CONTROL: { PregnantOrLactation: value } };
          });
          newAddInfo = next;
          return next;
        });
    }

    setOpenPregnantModal(false);
    if (pendingAddToPatient) {
      await performAddToPatient(newAddInfo || serviceAddInfo, nextServicesData);
    }
  };

  const handleHivSave = async (value: string) => {
    let newAddInfo: Record<string, any> | undefined = undefined;
    let nextServicesData = selectedServicesData;
    const servicesToUpdate: any[] = [];

    setSelectedServicesData((prev) => {
      const next = prev.map((service: any) => {
        const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
        const needs =
          controlType === "HIV Testi" ||
          controlType === "HIV" ||
          controlType === "HIV TEST" ||
          controlType === "Hiv Testi";
        if (needs) {
          servicesToUpdate.push(service);
          return { ...service, hivProcess: value };
        }
        return service;
      });
      nextServicesData = next;
      return next;
    });

    if (servicesToUpdate.length > 0) {
       setServiceAddInfo((prev) => {
          const next = { ...prev };
          servicesToUpdate.forEach((svc: any) => {
            const id = String(svc.process_id || svc.id);
            next[id] = { ...(next[id] || {}), HIV: { HivInfoId: value ? Number(value) : null } };
          });
          newAddInfo = next;
          return next;
        });
    }

    setOpenHivModal(false);
    if (pendingAddToPatient) {
      await performAddToPatient(newAddInfo || serviceAddInfo, nextServicesData);
    }
  };

  const handlePacemakerSave = async (value: string) => {
    let newAddInfo: Record<string, any> | undefined = undefined;
    let nextServicesData = selectedServicesData;
    const servicesToUpdate: any[] = [];

    setSelectedServicesData((prev) => {
      const next = prev.map((service: any) => {
        const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
        const needs =
          controlType === "Kalp Pili Kontrolü" ||
          controlType === "Kalp Pili" ||
          controlType === "Pacemaker" ||
          controlType === "Kalp Pili Kontrolu";
        if (needs) {
          servicesToUpdate.push(service);
          return { ...service, pacemakerInfo: value };
        }
        return service;
      });
      nextServicesData = next;
      return next;
    });

    if (servicesToUpdate.length > 0) {
       setServiceAddInfo((prev) => {
          const next = { ...prev };
          servicesToUpdate.forEach((svc: any) => {
            const id = String(svc.process_id || svc.id);
            next[id] = { ...(next[id] || {}), PACEMAKER_CONTROL: { PacemakerControl: value } };
          });
          newAddInfo = next;
          return next;
        });
    }

    setOpenPacemakerModal(false);
    if (pendingAddToPatient) {
      await performAddToPatient(newAddInfo || serviceAddInfo, nextServicesData);
    }
  };

  const handleLabSave = async (value: string) => {
    let newAddInfo: Record<string, any> | undefined = undefined;
    let nextServicesData = selectedServicesData;
    const servicesToUpdate: any[] = [];

    setSelectedServicesData((prev) => {
      const next = prev.map((service: any) => {
        const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
        const needs =
          controlType === "Laboratuvar" ||
          controlType === "Laboratory" ||
          controlType === "Lab" ||
          controlType === "LAB";
        if (needs) {
          servicesToUpdate.push(service);
          return { ...service, labSampleRegion: value };
        }
        return service;
      });
      nextServicesData = next;
      return next;
    });

    if (servicesToUpdate.length > 0) {
       setServiceAddInfo((prev) => {
          const next = { ...prev };
          servicesToUpdate.forEach((svc: any) => {
            const id = String(svc.process_id || svc.id);
            next[id] = { ...(next[id] || {}), LAB_REQUEST: { LabSampleTakenSiteId: value ? Number(value) : null } };
          });
          newAddInfo = next;
          return next;
        });
    }

    setOpenLabModal(false);
    if (pendingAddToPatient) {
      await performAddToPatient(newAddInfo || serviceAddInfo, nextServicesData);
    }
  };

  const handleInvestigationSave = async (note: string) => {
    let newAddInfo: Record<string, any> | undefined = undefined;
    let nextServicesData = selectedServicesData;
    const servicesToUpdate: any[] = [];

    setSelectedServicesData((prev) => {
      const next = prev.map((service: any) => {
        const controlType = service.control_type || service.controlType || service.hiz_ist_kont_turu;
        const needs =
          controlType === "Tetkik İstem Nedeni Zorunlu" ||
          controlType === "Tetkik Istem Nedeni Zorunlu" ||
          controlType === "Tetkik" ||
          controlType === "Investigation Reason";
        if (needs) {
          servicesToUpdate.push(service);
          return { ...service, investigationNote: note };
        }
        return service;
      });
      nextServicesData = next;
      return next;
    });

    if (servicesToUpdate.length > 0) {
       setServiceAddInfo((prev) => {
          const next = { ...prev };
          servicesToUpdate.forEach((svc: any) => {
            const id = String(svc.process_id || svc.id);
            next[id] = { ...(next[id] || {}), EXAMINATION_REQUEST: { ExaminationNote: note } };
          });
          newAddInfo = next;
          return next;
        });
    }

    setOpenInvestigationModal(false);
    if (pendingAddToPatient) {
      await performAddToPatient(newAddInfo || serviceAddInfo, nextServicesData);
    }
  };

  return {
    handleAddToPatientClick,
    handleVkiModalSave,
    handleEgfrModalSave,
    handleDirectionSave,
    handleAskDoctorSave,
    handlePregnantSave,
    handleHivSave,
    handlePacemakerSave,
    handleLabSave,
    handleInvestigationSave,
    upsertAddInfoForPendingService,
    doctorModalData,
    setDoctorModalData,
  };
}
